import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Modal } from '@asigna/kit';
import { createSubaccount, getAllMyMultisigs } from 'api';
import axios from 'axios';
import {
  ArchiveVaultDialog,
  AsignaProSubAccountsDialog,
  DeletePendingDialog,
  ReceiveBtcDialog,
  SubAccountCreateDialog,
  SubAccountErrorDialog,
} from 'components';
import { useSidebarState } from 'hooks';
import { useBreakpoints } from 'hooks/use-breakpoints';
import useNetwork from 'hooks/use-network';
import {
  StxChain,
  useAppsIframe,
  useArchiveVaultDialog,
  useCurrentSafe,
  useDeletePendingDialog,
  useProSubAccountsDialog,
  useSafes,
  useSidebarOpen,
  useSubAccountCreateDialog,
  useSubAccountErrorDialog,
} from 'store';

import { useLoadSafes } from './components/useLoadSafes';
import arrowIcon from './util/images/arrow.svg';
import swipeBtnIcon from './util/images/button.svg';
import { useSidebar } from './util/useSidebar';
import { SendAssetDialog, SidebarContent, SidebarWrapper } from './components';

interface SidebarProps {
  isSidebarDisplayed: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ isSidebarDisplayed }) => {
  const { setSafes, setIsSafesLoading } = useSafes();

  const { isMobileMenuOpen, setMobileMenuOpen, isReceiveBtcDialogOpen, setReceiveBtcDialogOpen } =
    useSidebar();

  const { onMobileZone: isMobile } = useBreakpoints();
  const { idDeletePendingDialogOpen, setDeletePendingDialogOpen } = useDeletePendingDialog();
  const { idArchiveVaultDialogOpen, setArchiveVaultDialogOpen } = useArchiveVaultDialog();
  const { idProSubAccountsDialogOpen, setProSubAccountsDialogOpen } = useProSubAccountsDialog();
  const { idSubAccountCreateDialogOpen, setSubAccountCreateDialogOpen } =
    useSubAccountCreateDialog();
  const { idSubAccountErrorDialogOpen, setSubAccountErrorDialogOpen } = useSubAccountErrorDialog();

  const { setDropdownOpen } = useSidebarOpen();

  const location = useLocation();
  const history = useHistory();

  const [network] = useNetwork();
  const { currentSafeId } = useSidebarState();
  const { currentSafe } = useCurrentSafe();

  const { isIframeRun, setIframeRun, isSidebarHidden, setSidebarHidden, setSelectedApp } =
    useAppsIframe();

  useLoadSafes(setIsSafesLoading);

  useEffect(() => {
    location.search !== '?iframe' && setSelectedApp('');
    location.search !== '?iframe' && setIframeRun(false);
    location.search !== '?iframe' && setSidebarHidden(false);
  }, [location]);

  const isUnsubscribePage = location.pathname.includes('unsubscribe');

  if (isUnsubscribePage) return null;

  if (isSidebarHidden) {
    return (
      <div>
        <button
          className="fixed w-[30px] h-[74px] top-1/2 transform -translate-y-1/2 z-20"
          onClick={() => {
            setSidebarHidden(!isSidebarHidden);
            if (isIframeRun) {
              setMobileMenuOpen(true);
            }
          }}
        >
          <img src={swipeBtnIcon} width={29} height={74} className="z-10" alt="" />
          <img
            src={arrowIcon}
            width={20}
            height={20}
            className="absolute z-20 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            alt="arrow"
          />
        </button>
      </div>
    );
  }

  return (
    <>
      <Modal
        onClose={() => setDeletePendingDialogOpen('')}
        isShow={Boolean(idDeletePendingDialogOpen)}
      >
        <DeletePendingDialog
          id={idDeletePendingDialogOpen}
          onClose={() => setDeletePendingDialogOpen('')}
        />
      </Modal>

      <SidebarWrapper
        isMobile={isMobile}
        isSidebarDisplayed={isSidebarDisplayed}
        isMobileMenuOpen={isMobileMenuOpen}
        handleMobileMenuOpen={setMobileMenuOpen}
      >
        <SidebarContent
          handleBreakIframe={() => {
            setMobileMenuOpen(false);
          }}
          handleReceiveBtcDialogOpen={() => {
            setMobileMenuOpen(false);
            setReceiveBtcDialogOpen(true);
          }}
          handleMobileMenuOpen={setMobileMenuOpen}
          handleSidebarHidden={() => setSidebarHidden(!isSidebarHidden)}
        />
        <Modal onClose={() => setReceiveBtcDialogOpen(false)} isShow={isReceiveBtcDialogOpen}>
          <ReceiveBtcDialog address="" onClose={() => setReceiveBtcDialogOpen(false)} />
        </Modal>
        <SendAssetDialog />

        <Modal
          onClose={() => setArchiveVaultDialogOpen('')}
          isShow={Boolean(idArchiveVaultDialogOpen)}
        >
          <ArchiveVaultDialog
            id={idArchiveVaultDialogOpen}
            onClose={() => setArchiveVaultDialogOpen('')}
          />
        </Modal>
        <Modal
          onClose={() => {
            setSubAccountCreateDialogOpen('');
          }}
          isShow={Boolean(idSubAccountCreateDialogOpen)}
        >
          <SubAccountCreateDialog
            onClose={() => {
              setSubAccountCreateDialogOpen('');
            }}
            onSuccess={async (name) => {
              const subAccountName = name;

              try {
                await createSubaccount(currentSafeId, subAccountName).then(async () => {
                  const net: StxChain =
                    network === 'testnet' ? StxChain.STX_TESTNET_CHAIN : StxChain.STX_MAINNET_CHAIN;
                  const resp = await getAllMyMultisigs(net);
                  setSafes(resp);
                });
              } catch (error: unknown) {
                if (axios.isAxiosError(error)) {
                  const backendMessage = error.response?.data?.message;
                  const backendList = error.response?.data?.existingAddresses;

                  if (backendList) {
                    setSubAccountErrorDialogOpen(backendList);
                  } else if (backendMessage) {
                    setSubAccountErrorDialogOpen([backendMessage]);
                  } else {
                    console.error('Error creating:', error.message);
                    setSubAccountErrorDialogOpen(['error']);
                  }
                } else {
                  console.error('Unknown error:', error);
                  setSubAccountErrorDialogOpen(['error']);
                }
              } finally {
                setSubAccountCreateDialogOpen('');
              }
            }}
          />
        </Modal>
        <Modal
          onClose={() => setSubAccountErrorDialogOpen([])}
          isShow={idSubAccountErrorDialogOpen.length > 0}
        >
          <SubAccountErrorDialog
            onClose={() => setSubAccountErrorDialogOpen([])}
            usedAddresses={idSubAccountErrorDialogOpen}
          />
        </Modal>
        <Modal
          onClose={() => setProSubAccountsDialogOpen('')}
          isShow={Boolean(idProSubAccountsDialogOpen)}
        >
          <AsignaProSubAccountsDialog
            onClose={() => setProSubAccountsDialogOpen('')}
            onSuccess={() => {
              setProSubAccountsDialogOpen('');
              setMobileMenuOpen(false);
              setDropdownOpen(false);
              history.push(`/vault/${currentSafe?.address}/pro`);
            }}
            id={idProSubAccountsDialogOpen}
          />
        </Modal>
      </SidebarWrapper>
    </>
  );
};
